import AddIcon from "@mui/icons-material/Add";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, InputAdornment, MenuItem, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import React, { useMemo, useState } from "react";

import { OUTIL_COLORS, paperSx, tableHeaderSx } from "./OutilContainer.styles";

const MONTHS = [
  ["jan", "Janvier"], ["feb", "Février"], ["mar", "Mars"],
  ["apr", "Avril"], ["may", "Mai"], ["jun", "Juin"],
  ["jul", "Juillet"], ["aug", "Août"], ["sep", "Septembre"],
  ["oct", "Octobre"], ["nov", "Novembre"], ["dec", "Décembre"],
];
const MONTH_HEADERS = ["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"];
const EMPTY_INDICATOR = {
  calculationMode: "", frequency: "", indicator: "", objective: "",
  processId: "", processLabel: "", source: "", year: new Date().getFullYear(),
  ...Object.fromEntries(MONTHS.map(([key]) => [key, ""])),
};
const SEARCH_FIELDS = [
  "processLabel", "indicator", "objective", "calculationMode", "source",
  "frequency", ...MONTHS.map(([key]) => key),
];
const BASE_COLUMNS = [
  ["Processus", 150], ["Indicateur", 170], ["Objectif", 120],
  ["Mode calcul", 145], ["Source", 130], ["Fréquence", 115],
];
const normalize = (value) => String(value || "").normalize("NFD")
  .replace(/[\u0300-\u036f]/g, "").toLocaleLowerCase("fr");
const sameId = (left, right) => String(left ?? "") === String(right ?? "");
const createId = () => `indicator-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const sectionSx = {
  backgroundColor: "#f8fafc", border: `1px solid ${OUTIL_COLORS.border}`,
  borderRadius: "8px", p: 1.4,
};
const goldButtonSx = {
  borderColor: OUTIL_COLORS.gold, color: OUTIL_COLORS.navy,
  fontSize: 10.5, fontWeight: 700, justifyContent: "flex-start",
  textTransform: "none", "&:hover": { backgroundColor: "#fff8df", borderColor: "#c79c2f" },
};

const FieldLabel = ({ children, htmlFor }) => (
  <Typography
    color="#526b84" component="label" display="block" fontSize={9.5}
    fontWeight={700} htmlFor={htmlFor} letterSpacing=".04em" mb={0.45}
    textTransform="uppercase"
  >
    {children}
  </Typography>
);
const InputField = ({ field, label, onChange, type = "text", value }) => (
  <Box>
    <FieldLabel htmlFor={`quality-${field}`}>{label}</FieldLabel>
    <TextField
      fullWidth id={`quality-${field}`}
      onChange={(event) => onChange(field, event.target.value)}
      size="small" type={type} value={value ?? ""}
    />
  </Box>
);

const IndicatorDialog = ({
  form,
  onChange,
  onClose,
  onDelete,
  onSave,
  open,
  processus,
  processusOptions,
}) => (
  <Dialog
    fullWidth maxWidth="lg" onClose={onClose} open={open}
    PaperProps={{ sx: { borderRadius: "14px", maxHeight: "92vh" } }}
  >
    <DialogTitle sx={{ alignItems: "center", borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", fontFamily: "Georgia, serif", fontSize: 17, fontWeight: 700, justifyContent: "space-between", px: 2.2, py: 1.4 }}>
      <Box component="span">
        <BarChartOutlinedIcon sx={{ color: "#31a7ba", fontSize: 19, mr: 0.8, verticalAlign: "middle" }} />
        Tableau de bord qualité — Fiche indicateur
      </Box>
      <Button color="error" onClick={onClose} size="small" startIcon={<CloseIcon />} sx={{ textTransform: "none" }} variant="outlined">Fermer</Button>
    </DialogTitle>

    <DialogContent sx={{ p: "18px !important" }}>
      <Box sx={{ display: "grid", gap: 1.2 }}>
        <Paper elevation={0} sx={{ alignItems: "center", backgroundColor: "#edf3f8", border: `1px solid ${OUTIL_COLORS.border}`, borderRadius: "7px", display: "flex", justifyContent: "space-between", px: 1.4, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10.5}>
            {form.id ? "Modification de l'indicateur sélectionné" : "Nouvel indicateur qualité"}
          </Typography>
          {form.id && <Button color="error" onClick={onDelete} size="small" startIcon={<DeleteOutlineIcon />} sx={{ fontSize: 10, textTransform: "none" }} variant="contained">Supprimer cet indicateur</Button>}
        </Paper>

        <Paper elevation={0} sx={sectionSx}>
          <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={800} sx={{ borderBottom: `1px solid ${OUTIL_COLORS.border}`, mb: 1.2, pb: 0.75 }} textTransform="uppercase">
            Identification de l'indicateur
          </Typography>
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))" } }}>
            <Box>
              <FieldLabel htmlFor="quality-process">Processus</FieldLabel>
              <TextField
                fullWidth id="quality-process" select size="small"
                onChange={(event) => {
                  const selected = processusOptions.find((item) => sameId(item.processus_id, event.target.value));
                  onChange("processId", event.target.value);
                  onChange("processLabel", selected?.instance || selected?.referentiel_nom || "");
                }}
                value={form.processId || processus?.processus_id || ""}
              >
                {processusOptions.map((item) => (
                  <MenuItem key={item.processus_id} value={item.processus_id}>
                    {item.instance || item.referentiel_nom || `Processus #${item.processus_id}`}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <InputField field="indicator" label="Indicateur" onChange={onChange} value={form.indicator} />
            <InputField field="objective" label="Objectif" onChange={onChange} value={form.objective} />
            <InputField field="calculationMode" label="Mode de calcul" onChange={onChange} value={form.calculationMode} />
            <InputField field="source" label="Source" onChange={onChange} value={form.source} />
            <InputField field="frequency" label="Fréquence" onChange={onChange} value={form.frequency} />
            <InputField field="year" label="Année" onChange={onChange} type="number" value={form.year} />
          </Box>
        </Paper>

        <Paper elevation={0} sx={sectionSx}>
          <Typography color={OUTIL_COLORS.navy} fontSize={10.5} fontWeight={800} sx={{ borderBottom: `1px solid ${OUTIL_COLORS.border}`, mb: 1.2, pb: 0.75 }} textTransform="uppercase">
            Valeurs mensuelles
          </Typography>
          <Box sx={{ display: "grid", gap: 1.1, gridTemplateColumns: { xs: "1fr 1fr", sm: "repeat(4, minmax(0, 1fr))", md: "repeat(6, minmax(0, 1fr))" } }}>
            {MONTHS.map(([key, label]) => (
              <InputField key={key} field={key} label={label} onChange={onChange} type="number" value={form[key]} />
            ))}
          </Box>
        </Paper>
      </Box>
    </DialogContent>

    <DialogActions sx={{ borderTop: `1px solid ${OUTIL_COLORS.border}`, px: 2.2, py: 1.25 }}>
      <Button onClick={onClose} sx={{ color: OUTIL_COLORS.text, textTransform: "none" }}>Annuler</Button>
      <Button onClick={onSave} startIcon={<SaveOutlinedIcon />} sx={{ backgroundColor: "#27884f", fontWeight: 700, textTransform: "none", "&:hover": { backgroundColor: "#1f7040" } }} variant="contained">Valider et sauvegarder</Button>
    </DialogActions>
  </Dialog>
);

const TableauBordQualite = ({
  onChange,
  processus,
  processusOptions = [],
  value = {},
}) => {
  const initialYear = Number(value.qualityDashboardYear) || new Date().getFullYear();
  const [year, setYear] = useState(initialYear);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_INDICATOR, year: initialYear });
  const rows = useMemo(
    () => (Array.isArray(value.qualityDashboardRows) ? value.qualityDashboardRows : []),
    [value.qualityDashboardRows]
  );
  const filteredRows = useMemo(() => {
    const yearRows = rows.filter((row) => Number(row.year) === Number(year));
    const query = normalize(search).trim();
    return query
      ? yearRows.filter((row) => SEARCH_FIELDS.some((field) => normalize(row[field]).includes(query)))
      : yearRows;
  }, [rows, search, year]);

  const setRows = (nextRows) => onChange("qualityDashboardRows", nextRows);
  const changeYear = (event) => {
    const nextYear = Number(event.target.value) || new Date().getFullYear();
    setYear(nextYear);
    onChange("qualityDashboardYear", nextYear);
  };
  const openCreate = () => {
    setForm({
      ...EMPTY_INDICATOR,
      processId: processus?.processus_id || "",
      processLabel: processus?.instance || processus?.referentiel_nom || "",
      year,
    });
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setForm({ ...EMPTY_INDICATOR, ...row });
    setDialogOpen(true);
  };
  const saveIndicator = () => {
    setRows(form.id
      ? rows.map((row) => (row.id === form.id ? { ...form } : row))
      : [...rows, { ...form, id: createId() }]);
    setDialogOpen(false);
  };
  const deleteIndicator = () => {
    if (form.id) setRows(rows.filter((row) => row.id !== form.id));
    setDialogOpen(false);
  };

  return (
    <>
      <Paper aria-label="Tableau de bord qualité" elevation={0} role="region" sx={{ ...paperSx, overflow: "hidden" }}>
        <Box sx={{ alignItems: "center", backgroundColor: OUTIL_COLORS.panelHeader, borderBottom: `1px solid ${OUTIL_COLORS.border}`, display: "flex", justifyContent: "space-between", minHeight: 52, px: 1.75 }}>
          <Typography color={OUTIL_COLORS.navy} fontSize={12} fontWeight={700}>
            <BarChartOutlinedIcon sx={{ color: "#31a7ba", fontSize: 16, mr: 0.7, verticalAlign: "middle" }} />
            Tableau de bord qualité
          </Typography>
          <Box sx={{ alignItems: "center", display: "flex", gap: 0.8 }}>
            <Typography color={OUTIL_COLORS.text} fontSize={10.5}>Année :</Typography>
            <TextField
              inputProps={{ min: 2000, max: 2100 }}
              onChange={changeYear} size="small" type="number"
              sx={{ width: 95, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 11, height: 34 } }}
              value={year}
            />
          </Box>
        </Box>

        <Box sx={{ alignItems: "center", backgroundColor: "#edf3f8", display: "flex", flexWrap: "wrap", gap: 1, px: 1.5, py: 1 }}>
          <Typography color={OUTIL_COLORS.textMuted} fontSize={9.5} fontWeight={700}>RECHERCHE :</Typography>
          <TextField
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16 }} /></InputAdornment> }}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Filtrer toutes les colonnes..." size="small"
            sx={{ flex: 1, minWidth: 220, "& .MuiInputBase-root": { backgroundColor: "#fff", fontSize: 10.5, height: 34 } }}
            value={search}
          />
          <Typography color={OUTIL_COLORS.textMuted} fontSize={10}>{filteredRows.length} entrée(s)</Typography>
          <Button disabled={!search} onClick={() => setSearch("")} size="small" startIcon={<RestartAltIcon />} sx={{ color: OUTIL_COLORS.navy, fontSize: 10, fontWeight: 700, textTransform: "none" }}>Réinit.</Button>
        </Box>

        <TableContainer sx={{ overflowX: "auto" }}>
          <Table aria-label="Indicateurs du tableau de bord qualité" size="small" sx={{ minWidth: 2100, tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                {BASE_COLUMNS.map(([label, width]) => <TableCell key={label} sx={{ ...tableHeaderSx, width }}>{label}</TableCell>)}
                {MONTH_HEADERS.map((label) => <TableCell key={label} sx={{ ...tableHeaderSx, width: 82 }}>{label}</TableCell>)}
                <TableCell sx={{ ...tableHeaderSx, width: 95 }}>Act.</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!filteredRows.length ? (
                <TableRow><TableCell align="center" colSpan={19} sx={{ color: "#8091a3", fontSize: 11, py: 2.2 }}>Aucun indicateur enregistré pour {year}.</TableCell></TableRow>
              ) : filteredRows.map((row) => (
                <TableRow hover key={row.id} onClick={() => openEdit(row)} sx={{ cursor: "pointer", "&:nth-of-type(even)": { backgroundColor: "#f7f9fc" } }}>
                  {[row.processLabel, row.indicator, row.objective, row.calculationMode, row.source, row.frequency].map((cell, index) => <TableCell key={BASE_COLUMNS[index][0]}>{cell || "—"}</TableCell>)}
                  {MONTHS.map(([key]) => <TableCell key={key}>{row[key] === "" || row[key] == null ? "—" : row[key]}</TableCell>)}
                  <TableCell>
                    <Tooltip title="Modifier">
                      <IconButton aria-label="Modifier l'indicateur" onClick={(event) => { event.stopPropagation(); openEdit(row); }} size="small" sx={{ backgroundColor: OUTIL_COLORS.navy, borderRadius: "6px", color: "#fff" }}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ p: 1 }}>
          <Button fullWidth onClick={openCreate} startIcon={<AddIcon />} sx={goldButtonSx} variant="outlined">
            Ajouter
          </Button>
        </Box>
      </Paper>

      <IndicatorDialog
        form={form}
        onChange={(field, nextValue) => setForm((current) => ({ ...current, [field]: nextValue }))}
        onClose={() => setDialogOpen(false)}
        onDelete={deleteIndicator}
        onSave={saveIndicator}
        open={dialogOpen}
        processus={processus}
        processusOptions={processusOptions}
      />
    </>
  );
};

export default TableauBordQualite;